import {Component, Inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FlexModule} from '@angular/flex-layout';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ManageIndustriesService} from '../manage-industries.service';
import {IndustryScopeType, SelectableCategory, SelectableIndustry} from '../industry.model';

interface DialogData {
  offeringUuid: string;
  categories: SelectableCategory[];
  existingCodes: string[];
}

@Component({
  selector: 'app-add-industry-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButton,
    MatFormField,
    MatInput,
    FlexModule,
    ReactiveFormsModule,
    MatHint,
    MatError
  ],
  templateUrl: './add-industry-dialog.component.html',
  styleUrl: './add-industry-dialog.component.scss'
})
export class AddIndustryDialogComponent {

  industryForm: FormGroup;
  isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<AddIndustryDialogComponent>,
    // @ts-ignore
    @Inject(MAT_DIALOG_DATA) protected data: DialogData,
    private fb: FormBuilder,
    private industriesService: ManageIndustriesService
  ) {
    this.industryForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]]
      // Removed categoryId and code fields as they will be handled differently
    });

    // Auto-generate code from name
    this.industryForm.get('name')?.valueChanges.subscribe(name => {
      if (name && !this.industryForm.get('code')?.touched) {
        const generatedCode = this.generateCodeFromName(name);
        // We don't set the code field directly since it's not in the form, but we'll use it in onSave
      }
    });
  }

  private generateCodeFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  }

  private uniqueCodeValidator(control: any) {
    const value = control.value;
    if (value && this.data.existingCodes.includes(value)) {
      return {uniqueCode: true};
    }
    return null;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.industryForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formValue = this.industryForm.value;
      // Get the pre-selected category from data
      const selectedCategory = this.data.categories[0]; // Since we only pass one category

      // Generate code based on name and offering UUID
      const generatedCode = this.generateCodeFromName(formValue.name) + '-' + this.data.offeringUuid.substring(0, 8);

      const newIndustry: SelectableIndustry = {
        code: generatedCode,
        name: formValue.name,
        description: formValue.description,
        icon: 'business',
        typeScope: IndustryScopeType.OFFERING,
        offeringUuid: this.data.offeringUuid,
        industryCategoryId: selectedCategory.id, // Use the pre-selected category ID
        selected: true
      };

      // Fix: Check if selectedCategory.id is defined before using it
      if (selectedCategory.id !== undefined) {
        this.industriesService.saveOfferingIndustry(newIndustry, selectedCategory.id).subscribe({
          next: (savedIndustry) => {
            this.isLoading = false;
            // Convert to SelectableIndustry format
            const selectableIndustry: SelectableIndustry = {
              ...savedIndustry,
              selected: true
            };
            this.dialogRef.close({
              categoryId: selectedCategory.id,
              industry: selectableIndustry
            });
          },
          error: (error) => {
            this.isLoading = false;
            console.error('Error saving industry:', error);
            // Show error message
          }
        });
      } else {
        console.error('Selected category ID is undefined');
        this.isLoading = false;
      }
    }
  }

  getErrorMessage(fieldName: string): string {
    const field = this.industryForm.get(fieldName);
    if (field?.hasError('required')) {
      return `${fieldName} is required`;
    }
    if (field?.hasError('minlength')) {
      return `${fieldName} must be at least 2 characters`;
    }
    if (field?.hasError('maxlength')) {
      return `${fieldName} must not exceed 500 characters`;
    }
    return '';
  }
}
